import { useState, useRef, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";

// Admin-assisted phone verification for a TEAM MEMBER (not the caller's own number).
// Texts a 6-digit code to the member's mobile and confirms it via the /admins/{uid}/phone
// endpoints — so a manager/super-admin can get a member's number verified without that member
// hunting through their own settings (e.g. when it's the same person on two logins).
// `showInput` lets the caller supply the number field (Emergency) or drive it externally (Team).
export function AdminPhoneVerify({ userId, initialPhone = "", verified = false, showInput = true, onVerified }) {
  const [phone, setPhone] = useState(initialPhone || "");
  const [codeSent, setCodeSent] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const [done, setDone] = useState(verified);
  const timerRef = useRef(null);

  useEffect(() => () => clearInterval(timerRef.current), []);
  useEffect(() => {
    setPhone(initialPhone || ""); setDone(verified); setCodeSent(false); setCode("");
  }, [initialPhone, verified, userId]);

  const startCooldown = (s) => {
    setCooldown(s);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => setCooldown((c) => { if (c <= 1) { clearInterval(timerRef.current); return 0; } return c - 1; }), 1000);
  };

  const sendCode = async () => {
    setBusy("send");
    try {
      const r = await api.post(`/admins/${userId}/phone/send-code`, { phone });
      setCodeSent(true); startCooldown(r.data.cooldown || 45);
      toast[r.data.sent ? "success" : "message"](r.data.sent
        ? "Code texted to that number"
        : "We tried to text that number — enter the code if it arrives.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't send a code."); }
    setBusy("");
  };

  const verify = async () => {
    setBusy("verify");
    try {
      const r = await api.post(`/admins/${userId}/phone/verify`, { code });
      setDone(true); setCodeSent(false); setCode("");
      toast.success("Mobile verified — this number can now receive text and call alerts.");
      onVerified?.(r.data.phone);
    } catch (e) { toast.error(e?.response?.data?.detail || "Verification failed."); }
    setBusy("");
  };

  if (done) {
    return (
      <div data-testid="admin-phone-verified" className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold px-2.5 py-1">
        <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={2.5} /> Mobile verified
      </div>
    );
  }

  return (
    <div data-testid="admin-phone-verify" className="rounded-xl border border-amber-200 bg-amber-50/60 p-3 space-y-2">
      <div className="text-xs text-amber-900 leading-snug">
        <b>Not verified.</b> We won&rsquo;t text or call this number until it&rsquo;s confirmed with a texted code.
        Send the code, then enter it below. If this is your own number, the code comes to your phone.
      </div>
      <div className="flex flex-col sm:flex-row gap-2">
        {showInput && (
          <input data-testid="admin-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)}
            placeholder="+15125550123"
            className="min-w-0 flex-1 rounded-lg border border-sand-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-redline-500/30" />
        )}
        <button data-testid="admin-phone-send-code" onClick={sendCode}
          disabled={!phone || busy === "send" || cooldown > 0}
          className="rounded-lg bg-ink text-white text-xs font-semibold px-3 py-2 hover:bg-ink-700 transition-colors disabled:opacity-50 whitespace-nowrap">
          {busy === "send" ? "Sending…" : cooldown > 0 ? `Resend ${cooldown}s` : codeSent ? "Resend code" : "Send verification code"}
        </button>
      </div>
      {codeSent && (
        <div className="flex flex-col sm:flex-row gap-2">
          <input data-testid="admin-phone-code" value={code} inputMode="numeric"
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            placeholder="6-digit code"
            className="min-w-0 flex-1 rounded-lg border border-sand-200 px-3 py-2 text-sm tracking-[0.3em] font-semibold outline-none focus:ring-2 focus:ring-redline-500/30" />
          <button data-testid="admin-phone-verify-btn" onClick={verify}
            disabled={code.length !== 6 || busy === "verify"}
            className="rounded-lg bg-redline-500 text-white text-xs font-semibold px-4 py-2 hover:bg-redline-600 transition-colors disabled:opacity-50 whitespace-nowrap">
            {busy === "verify" ? "Verifying…" : "Verify"}
          </button>
        </div>
      )}
    </div>
  );
}
